import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  getUrlParams,
  setUrlParams,
} from "../../../utils/helpers/setAndGetUrlParams";
import { all_routes } from "../../../feature-module/router/all_routes";
import { useSelector } from "react-redux";
import { generateQueryParams } from "../../../utils/helpers/generateQueryParams";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { GrPrevious, GrNext } from "react-icons/gr";
import { useEffect, useState } from "react";
import { useLeadsData } from "../../../feature-module/crm/leads/hooks/useLeadsData";
import { useDealsData } from "../../../feature-module/crm/deals/hooks/useDealsData";
import LeadApi from "../../../feature-module/crm/leads/api/LeadApi";
import DealApi from "../../../feature-module/crm/deals/api/DealApi";

const DetailsNavigator = ({ module, currentModuleId }) => {
  const route = all_routes;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const isLeads = location.pathname.includes("leads");
  const isDeals = location.pathname.includes("deals");

  const { filters } = useSelector((state) => state.filter);
  const leadsFilters = filters.leads || [];
  const dealFilters = filters.deals || [];

  let { queryParams, params } =
    module === "leads"
      ? generateQueryParams(leadsFilters)
      : generateQueryParams(dealFilters);

  const initialPage = Number(getUrlParams("page")) || 1;
  const [page, setPage] = useState(initialPage);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const newPage = Number(getUrlParams("page")) || 1;
    setPage(newPage);
  }, [searchParams]);

  const limit = Number(getUrlParams("limit")) || 50;
  const sort = module === "leads" ? "-createdTime" : "-leadToDealOn";
  const ofCompany = getUrlParams("companyid") || undefined;

  const queryString = params?.toString() || "";
  queryParams = queryParams?.toString() || "";

  const { data: leadsData } = useLeadsData({
    page,
    limit,
    queryString: params?.toString() || "",
    queryParams: queryParams?.toString() || "",
    ofCompany,
    sort,
    enabled: page > 0 && isLeads,
  });

  const { data: dealsData } = useDealsData({
    page,
    limit,
    queryString: params?.toString() || "",
    queryParams: queryParams?.toString() || "",
    ofCompany,
    sort,
    enabled: page > 0 && isDeals,
  });

  // ✅ Use cached data for quick loading
  useEffect(() => {
    if (module === "leads") {
      setData(leadsData?.data?.data || []);
      setTotalCount(leadsData?.data?.totalResult || 0);
    } else {
      setData(dealsData?.data?.data || []);
      setTotalCount(dealsData?.data?.totalResult || 0);
    }
  }, [module, leadsData, dealsData, page, ofCompany, limit]);

  if (!data?.length) return null;

  // ✅ Calculate absolute index based on page and limit
  const currentIndex =
    (page - 1) * limit + data.findIndex((item) => item._id === currentModuleId);

  const prevModuleDetails =
    currentIndex > (page - 1) * limit
      ? data[currentIndex - (page - 1) * limit - 1]
      : null;

  const nextModuleDetails =
    currentIndex < page * limit - 1
      ? data[currentIndex - (page - 1) * limit + 1]
      : null;

  // ✅ Handle Previous Button
  const handlePrev = async () => {
    if (prevModuleDetails) {
      // ✅ Navigate to previous item on the same page
      navigate(
        `${route[`${module}Details`].replace(":id", prevModuleDetails._id)}`
      );
    } else if (currentIndex === (page - 1) * limit && page > 1) {
      // ✅ Load previous page and navigate to last record
      const newPage = page - 1;
      setUrlParams(setSearchParams, "page", newPage);
      setPage(newPage);

      // ✅ Safe fallback for apiFunction
      let apiFunction;
      if (module === "leads") {
        apiFunction = LeadApi.fetchLeads;
      } else if (module === "deals") {
        apiFunction = DealApi.fetchDeals;
      }

      if (!apiFunction) {
        console.error(`Invalid module: ${module}`);
        return;
      }

      try {
        // ✅ Use fetchQuery to get data directly
        const newData = await queryClient.fetchQuery({
          queryKey: [
            module,
            newPage,
            limit,
            queryString,
            queryParams,
            ofCompany,
            sort,
          ],
          queryFn: () =>
            apiFunction({
              page: newPage,
              limit,
              queryString,
              queryParams,
              ofCompany,
              populate: "ofCompany,assignedTo",
              selectPopulate: "name",
              sort,
            }),
        });

        if (newData?.data?.data?.length) {
          navigate(
            `${route[`${module}Details`].replace(
              ":id",
              newData.data.data[newData.data.data.length - 1]._id
            )}`
          );
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
    }
  };

  const handleNext = async () => {
    if (nextModuleDetails) {
      // ✅ Navigate to next item on the same page
      navigate(
        `${route[`${module}Details`].replace(":id", nextModuleDetails._id)}`
      );
    } else if (currentIndex === page * limit - 1 && totalCount > page * limit) {
      // ✅ Load next page and navigate to first record
      const newPage = page + 1;
      setUrlParams(setSearchParams, "page", newPage);
      setPage(newPage);

      // ✅ Safe fallback for apiFunction
      let apiFunction;
      if (module === "leads") {
        apiFunction = LeadApi.fetchLeads;
      } else if (module === "deals") {
        apiFunction = DealApi.fetchDeals;
      }

      if (!apiFunction) {
        console.error(`Invalid module: ${module}`);
        return;
      }

      try {
        // ✅ Use fetchQuery to get data directly
        const newData = await queryClient.fetchQuery({
          queryKey: [
            module,
            newPage,
            limit,
            queryString,
            queryParams,
            ofCompany,
            sort,
          ],
          queryFn: () =>
            apiFunction({
              page: newPage,
              limit,
              queryString,
              queryParams,
              ofCompany,
              populate: "ofCompany,assignedTo",
              selectPopulate: "name",
              sort,
            }),
        });

        if (newData?.data?.data?.length) {
          navigate(
            `${route[`${module}Details`].replace(
              ":id",
              newData.data.data[0]._id
            )}`
          );
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
    }
  };

  return (
    <div className="d-flex gap-1 justify-content-center align-items-center">
      <OverlayTrigger placement="bottom" overlay={<Tooltip>Previous</Tooltip>}>
        <button
          onClick={handlePrev}
          disabled={page === 1 && currentIndex === 0}
          className="btn btn-sm btn-primary rounded-end-0"
        >
          <GrPrevious size={22} />
        </button>
      </OverlayTrigger>
      <OverlayTrigger placement="bottom" overlay={<Tooltip>Next</Tooltip>}>
        <button
          onClick={handleNext}
          disabled={currentIndex === totalCount - 1} // ✅ Fix the condition
          className="btn btn-sm btn-primary rounded-start-0"
        >
          <GrNext size={22} />
        </button>
      </OverlayTrigger>
    </div>
  );
};

export default DetailsNavigator;
